import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts'
import { audioService } from '@/modules/chatting/service/audio'
import type { Audio } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

import { DialogProps } from '../types'
import {
  EDIT_AUDIO_FORM_ID,
  EditAudioFormValues,
  editAudioFormResolver,
} from './validation'

interface Props extends DialogProps {
  audio: Audio
}

export const EditAudioDialog = ({ isOpen, onClose, audio }: Props) => {
  const form = useForm<EditAudioFormValues>({
    resolver: editAudioFormResolver,
    defaultValues: {
      name: audio.name,
      description: audio.description,
    },
  })
  const creatorId = useUnit($creatorId) ?? 0
  const queryClient = useQueryClient()

  const editAudioMutation = useMutation(
    ({ name, description }: EditAudioFormValues) =>
      audioService.editAudio({
        name,
        description,
        id: audio.id,
        creatorId: creatorId,
      }),
    {
      onError: (error) => {
        handleError(error, 'There was an error editing this audio.')
      },
      onSuccess: () => {
        onClose()
        toast.success('Successfully saved audio.')
        queryClient.invalidateQueries(['audios', creatorId])
      },
    },
  )

  return (
    <Dialog
      panelClassName="lg:max-w-[533px]"
      show={isOpen}
      onClose={onClose}
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title>Edit Audio</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-6 block">
        <FormProvider {...form}>
          <form
            className="space-y-5"
            id={EDIT_AUDIO_FORM_ID}
            onSubmit={form.handleSubmit((values) =>
              editAudioMutation.mutateAsync(values),
            )}
          >
            <FormField
              name="name"
              label="Title"
            />
            <FormField
              name="description"
              label="Description"
              placeholder="Optional"
            />
          </form>
        </FormProvider>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          form={EDIT_AUDIO_FORM_ID}
          isLoading={form.formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
